import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../store/store";
import { fetchStepperData, getPortalDataHealthIndia } from "../../../../models/preAuthSlice";
import {
  approveHealthIndiaPreAuth,
  buildHealthIndiaApprovePayload,
  buildHealthIndiaPreAuthPayload,
  HEALTH_INDIA_DATE_FORMAT,
  initiateCasePreAuthHealthIndia,
  intimateHealthIndiaPreAuth,
  saveDraftHealthIndia,
  updateDraftHealthIndia,
} from "../../../../apis/SchemeService/HEALTHINDIA/healthIndiaService";
import PreAuthHealthIndia from "../../../../components/patient/PreAuth/HealthIndia/PreAuthHealthIndia";
import { getBasicDetails, getDeficiencyQuestionData, getTimelineDetails } from "../../../../models/patientSlice";
import { Form, message } from "antd";
import dayjs from "dayjs";
import { errorMessage, successMessage } from "../../../../utils/notificationUtils";
import {
  deleteUploadedDocument,
  getAllStarhealthDocumentsData,
  updateAllDocumentsData,
} from "../../../../apis/patientService";
import { UploadDocument } from "../../../../models/EnhancementSlice";
import FilePreviewModal from "../../../../components/patient/PreAuth-query/FilePreviewModal";
import MultipleFilesPreviewModal from "../../../../components/patient/PreAuth-query/MultipleFilesPreviewModal";
import PatientDetailsStageDocument from "../../../../components/modals/PatientDetailsStageDocument";
import DocumentDeficiencyModal from "../../../../components/patient/DocumentDeficiencyModal";
import ConfirmSubmissionModalStage from "../../../../components/modals/ConfirmSubmissionModalStage";
import ManuallyClaimIdPortal from "../../../../components/patient/PreAuth/ManuallyClaimIdPortal";
import PortalClaimValidationModal from "../../../../components/patient/Portal Registration/PortalClaimValidationModal";
import {
  handlePortalClaimValidationFlow,
  updatePortalClaimValidation,
} from "../../../../utils/portalClaimValidation";

type PreAuthHealthIndiaContainerProps = {
  activeKey: any;
  sseData?: any;
  sseUpdateCount?: any;
};

const MANDATORY_FIELDS_ERROR = "Please, fill all mandatory fields";

const PreAuthHealthIndiaContainer: FC<PreAuthHealthIndiaContainerProps> = ({
  activeKey,
  sseData,
  sseUpdateCount,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const { patients, preAuth, auth, enhancement } = useSelector((state: RootState) => state);
  const [isSaving, setIsSaving] = useState(false);
  const [isIntimating, setIsIntimating] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [manuallyClaimIdPortal, setManuallyClaimIdPortal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [documentRequestResponseDocuments, setDocumentRequestResponseDocuments] = useState<any>();
  const [documentUrls, setDocumentUrls] = useState<any[]>([]);
  const [previewDocument, setPreviewDocument] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [deficiencyModal, setDeficiencyModal] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [clickedActiveTab, setClickedActiveTab] = useState<any>();
  const [clickedActiveDocumentUrl, setClickedActiveDocumentUrl] = useState<any>();
  const [fetchingDeficiencyData, setFetchingDeficiencyData] = useState(false);

  const [selectedFile, setSelectedFile] = useState<{ file: File; documentType: string } | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<{ file: File; documentType: string }[]>([]);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState({
    url: "",
    name: "",
    showUpload: false,
    documentType: "",
  });
  const [previewFiles, setPreviewFiles] = useState<any[]>([]);
  const [uploadFileList, setUploadFileList] = useState<any[]>([]);
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const [confirmationState, setConfirmationState] = useState<string>("");
  const [confirmationTitle, setConfirmationTitle] = useState<string>("");
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [validationRes, setValidationRes] = useState<any>(null);
  const [isValidationLoading, setIsValidationLoading] = useState(false);
  const [registrationValidationStatus, setRegistrationValidationStatus] =
    useState<any>(null);

  const healthIndiaPortalData =
    preAuth?.portalData?.casePreAuthOthersResponse?.data ||
    preAuth?.portalData?.casePreAuthHealthIndia?.data;

  const isPreAuthTabActive = () => {
    if (!activeKey || activeKey.length === 0) return false;

    const currentStageId =
      typeof activeKey === "string"
        ? parseInt(activeKey)
        : parseInt(activeKey?.[0]);

    const currentStage = preAuth?.stepperData?.visitStages?.find(
      (stage: any) => stage?.visitStageId === currentStageId,
    );

    return currentStage?.visitStage?.toUpperCase() === "PREAUTH";
  };

  const fetchStepperForStatus = () => {
    if (patients?.fetchBasicDetails?.casesResponse?.id) {
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      dispatch(fetchStepperData({ caseId, visitId }));
    }
  };

  const fetchPreAuthData = async () => {
    try {
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      if (!caseId || !visitId) return;

      await dispatch(getPortalDataHealthIndia({ caseId, visitId }));
    } catch (err: any) {
      errorMessage(err?.data?.message || "Failed to fetch Health India PreAuth data");
    }
  };

  const fetchDocuments = async () => {
    const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
    const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
    const schemeMetaDataId = patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId;
    if (!caseId || !visitId || !schemeMetaDataId) return;

    try {
      setLoading(true);
      const allDocumentList = await getAllStarhealthDocumentsData(caseId, visitId, schemeMetaDataId);
      setDocumentTypes(allDocumentList?.["PreAuth"] || []);
      setDocumentUrls(
        (allDocumentList?.["PreAuth"] || [])
          .filter((item: any) => item.documentList && item.documentList.length > 0)
          .flatMap((item: any) =>
            item.documentList.map((doc: any) => ({
              ...doc,
              documentLabelType: doc.documentLabelType,
            }))
          )
      );
    } catch (err: any) {
      errorMessage(err?.message || "Failed to fetch Health India documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      patients?.fetchBasicDetails?.casesResponse?.id &&
      patients?.fetchBasicDetails?.visitInfoResponse?.id &&
      activeKey &&
      activeKey?.length > 0 &&
      isPreAuthTabActive()
    ) {
      fetchPreAuthData();
    }
  }, [
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
    activeKey,
    preAuth?.stepperData,
  ]);

  useEffect(() => {
    if (
      patients?.fetchBasicDetails?.casesResponse?.id &&
      patients?.fetchBasicDetails?.visitInfoResponse?.id &&
      activeKey &&
      activeKey?.length > 0 &&
      isPreAuthTabActive()
    ) {
      fetchDocuments();
    }
  }, [
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
    patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
    activeKey,
    preAuth?.stepperData,
  ]);

  useEffect(() => {
    const requestResponseDocuments = {
      requestedDocuments: [] as any[],
      responseDocuments: [] as any[],
    };
    documentTypes?.forEach((document) => {
      if (document?.requestDocument) requestResponseDocuments.requestedDocuments.push(document);
      if (document?.responseDocument) requestResponseDocuments.responseDocuments.push(document);
    });
    setDocumentRequestResponseDocuments(requestResponseDocuments);
  }, [documentTypes]);

  useEffect(() => {
    if (!isPreAuthTabActive() || !healthIndiaPortalData) return;
    form.setFieldsValue({
      admissionDate: healthIndiaPortalData?.admissionDate
        ? dayjs(healthIndiaPortalData.admissionDate, HEALTH_INDIA_DATE_FORMAT)
        : null,
      dischargeDate: healthIndiaPortalData?.dischargeDate
        ? dayjs(healthIndiaPortalData.dischargeDate, HEALTH_INDIA_DATE_FORMAT)
        : null,
      stayInHospitalDays:
        healthIndiaPortalData?.lengthOfStay || healthIndiaPortalData?.stayInHospitalDays,
      diagnosisAilment: healthIndiaPortalData?.diagnosis,
      claimedAmount: healthIndiaPortalData?.requestedAmount,
      treatingDoctorName: healthIndiaPortalData?.doctorName,
    });
  }, [healthIndiaPortalData, form, activeKey, preAuth?.stepperData]);

  const getTimelineDetail = async () => {
    try {
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      if (!caseId || !visitId) return;

      await dispatch(getTimelineDetails({ caseId, visitId }));
    } catch (err: any) {
      errorMessage(err?.message || "Failed to fetch timeline");
    }
  };

  const validateRequiredDocuments = (
    mandatoryDocuments: any,
    documentUrlsList: any
  ) => {
    for (const doc of mandatoryDocuments || []) {
      if (doc.required) {
        const isUploaded = documentUrlsList?.some(
          (uploadedDoc: any) => uploadedDoc.documentLabelType === doc.label
        );
        if (!isUploaded) {
          return false;
        }
      }
    }
    return true;
  };

  const buildPayloadFromForm = async (action: string = "save") => {
    const values = form.getFieldsValue(true);
    const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
    const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
    const hospitalId = patients?.fetchBasicDetails?.casesResponse?.healthCareId;
    const schemeMetaDataId = patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId;
    const portalMetaDataId = patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0];
    const existingCasePreAuthId =
      healthIndiaPortalData?.casePreAuthResponse?.id || 0;

    const formattedValues = {
      admissionDate: values?.admissionDate
        ? dayjs(values.admissionDate).format(HEALTH_INDIA_DATE_FORMAT)
        : null,
      dischargeDate: values?.dischargeDate
        ? dayjs(values.dischargeDate).format(HEALTH_INDIA_DATE_FORMAT)
        : null,
      stayInHospitalDays: values?.stayInHospitalDays,
      diagnosisAilment: values?.diagnosisAilment,
      claimedAmount: values?.claimedAmount,
      treatingDoctorName: values?.treatingDoctorName,
      documentsList: documentUrls,
    };

    const payload = buildHealthIndiaPreAuthPayload({
      schemeMetaDataId,
      portalMetaDataId,
      casePreAuthId: existingCasePreAuthId,
      hospitalId,
      schemeId: schemeMetaDataId,
      beneficiaryId:
        preAuth?.portalData?.beneficiaryId ||
        patients?.fetchBasicDetails?.casesResponse?.memberId,
      patientOpdIpdNumber: patients?.fetchBasicDetails?.visitInfoResponse?.opNumber,
      integratedWithPortal: patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal,
      action,
      formValues: formattedValues,
    });

    return { payload, caseId, visitId, existingCasePreAuthId, portalMetaDataId };
  };

  const getBasePayload = async (action: string = "save") => buildPayloadFromForm(action);

  const refreshAll = async () => {
    try {
      await fetchPreAuthData();
      await fetchDocuments();
      fetchStepperForStatus();
      await getTimelineDetail();
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      if (visitId) await dispatch(getBasicDetails({ id: visitId }));
    } catch (err: any) {
      errorMessage(err?.message || "Failed to refresh stage data");
    }
  };

  const onSave = async () => {
    try {
      setIsSaving(true);
      const { payload, caseId, visitId } = await getBasePayload("save");
      if (!caseId || !visitId) return;
      await saveDraftHealthIndia(caseId, visitId, payload);
      successMessage("PreAuth saved successfully");
      await refreshAll();
    } catch (e: any) {
      errorMessage(e?.message || "Failed to save PreAuth");
    } finally {
      setIsSaving(false);
    }
  };

  const onUpdate = async () => {
    try {
      setIsSaving(true);
      const { payload, caseId, visitId, existingCasePreAuthId } = await getBasePayload("save");
      if (!caseId || !visitId || !existingCasePreAuthId) return;
      await updateDraftHealthIndia(caseId, visitId, existingCasePreAuthId, payload);
      successMessage("PreAuth updated successfully");
      await refreshAll();
    } catch (e: any) {
      errorMessage(e?.message || "Failed to update PreAuth");
    } finally {
      setIsSaving(false);
    }
  };

  const onIntimate = async () => {
    try {
      if (
        !validateRequiredDocuments(
          documentRequestResponseDocuments?.requestedDocuments,
          documentUrls
        )
      ) {
        errorMessage("Please, Upload all required documents");
        return;
      }
      setIsIntimating(true);
      const { payload, caseId, visitId } = await getBasePayload("intimate");
      if (!caseId || !visitId) return;
      await intimateHealthIndiaPreAuth(caseId, visitId, payload);
      successMessage("PreAuth sent to QC successfully");
      await refreshAll();
    } catch (e: any) {
      errorMessage(e?.message || "Failed to intimate PreAuth");
    } finally {
      setIsIntimating(false);
    }
  };

  const integratedWithPortal =
    patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal ?? true;

  const onInitiate = async () => {
    try {
      await form.validateFields();
      if (
        !validateRequiredDocuments(
          documentRequestResponseDocuments?.requestedDocuments,
          documentUrls
        )
      ) {
        errorMessage("Please, Upload all required documents");
        return;
      }
      setIsSubmitting(true);
      const { payload, caseId, visitId } = await buildPayloadFromForm("save");
      if (!caseId || !visitId) return;

      const initiatePayload = {
        ...payload,
        casePreAuthId: healthIndiaPortalData?.casePreAuthResponse?.id || 0,
        integratedWithPortal: true,
      };

      await initiateCasePreAuthHealthIndia(caseId, visitId, initiatePayload);
      successMessage("PreAuth initiated on portal successfully");
      await refreshAll();
    } catch (e: any) {
      if (e?.errorFields) {
        errorMessage(MANDATORY_FIELDS_ERROR);
      } else {
        errorMessage(e?.message || "Failed to initiate PreAuth on portal");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmitManually = async (values: { manualId: string }) => {
    try {
      await form.validateFields();
      if (
        !validateRequiredDocuments(
          documentRequestResponseDocuments?.requestedDocuments,
          documentUrls
        )
      ) {
        errorMessage("Please, Upload all required documents");
        return;
      }
      setIsSubmitting(true);
      const { payload, caseId, visitId } = await buildPayloadFromForm("save");
      if (!caseId || !visitId) return;

      const manualPayload = {
        ...payload,
        casePreAuthId: healthIndiaPortalData?.casePreAuthResponse?.id || 0,
        integratedWithPortal: false,
        casePreAuthOthersRequest: {
          ...payload.casePreAuthOthersRequest,
          claimId: values?.manualId,
        },
      };

      await initiateCasePreAuthHealthIndia(caseId, visitId, manualPayload);
      successMessage("PreAuth submitted manually successfully");
      setManuallyClaimIdPortal(false);
      setConfirmationModal(false);
      setConfirmationState("");
      await refreshAll();
    } catch (e: any) {
      if (e?.errorFields) {
        errorMessage(MANDATORY_FIELDS_ERROR);
      } else {
        errorMessage(e?.message || "Failed to submit PreAuth manually");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      if (
        !validateRequiredDocuments(
          documentRequestResponseDocuments?.requestedDocuments,
          documentUrls
        )
      ) {
        errorMessage("Please, Upload all required documents");
        return;
      }
      if (integratedWithPortal) {
        handleSaveConfirmationModal("initiate");
      } else {
        handleSaveConfirmationModal("submit");
      }
    } catch (err: any) {
      if (err?.errorFields) {
        errorMessage(MANDATORY_FIELDS_ERROR);
      } else {
        errorMessage(err?.message || "Please try again later");
      }
    }
  };

  const onApprove = async () => {
    try {
      await form.validateFields();
      if (
        !validateRequiredDocuments(
          documentRequestResponseDocuments?.requestedDocuments,
          documentUrls
        )
      ) {
        errorMessage("Please, Upload all required documents");
        return;
      }
      setIsApproving(true);
      const { payload, caseId, visitId, existingCasePreAuthId, portalMetaDataId } =
        await getBasePayload("save");
      if (!caseId || !visitId) return;

      const approvalPayload = buildHealthIndiaApprovePayload({
        caseId,
        visitId,
        stage: "PreAuth",
        approved: true,
        casePreAuthId: existingCasePreAuthId || 0,
        portalMetaDataId,
        preAuthRequest: payload,
      });

      await approveHealthIndiaPreAuth(approvalPayload);
      successMessage("PreAuth Approved Successfully");
      await refreshAll();
    } catch (e: any) {
      if (e?.errorFields) {
        errorMessage(MANDATORY_FIELDS_ERROR);
      } else {
        errorMessage(e?.message || "Failed to approve PreAuth");
      }
    } finally {
      setIsApproving(false);
    }
  };

  const handleSaveConfirmationModal = (
    nextState: "save" | "update" | "intimate" | "approve" | "initiate" | "submit"
  ) => {
    if (
      nextState === "intimate" ||
      nextState === "initiate" ||
      nextState === "submit"
    ) {
      if (
        !validateRequiredDocuments(
          documentRequestResponseDocuments?.requestedDocuments,
          documentUrls
        )
      ) {
        errorMessage("Please, Upload all required documents");
        return;
      }
    }
    setConfirmationState(nextState);
    setConfirmationModal(true);
    if (nextState === "save") {
      setConfirmationTitle("Do You want to save PreAuth ?");
    } else if (nextState === "update") {
      setConfirmationTitle("Do You want to update PreAuth ?");
    } else if (nextState === "intimate") {
      setConfirmationTitle("Do you want to send PreAuth for QC ?");
    } else if (nextState === "approve") {
      setConfirmationTitle("Do You want to approve PreAuth ?");
    } else if (nextState === "initiate") {
      setConfirmationTitle("Do you want to submit PreAuth on portal ?");
    } else if (nextState === "submit") {
      setConfirmationTitle("Do you want to submit PreAuth ?");
    }
  };

  const handleConfirmAction = async () => {
    if (confirmationState === "save") {
      await onSave();
      setConfirmationModal(false);
      setConfirmationState("");
    } else if (confirmationState === "update") {
      await onUpdate();
      setConfirmationModal(false);
      setConfirmationState("");
    } else if (confirmationState === "intimate") {
      await onIntimate();
      setConfirmationModal(false);
      setConfirmationState("");
    } else if (confirmationState === "approve") {
      await onApprove();
      setConfirmationModal(false);
      setConfirmationState("");
    } else if (confirmationState === "initiate") {
      await onInitiate();
      setConfirmationModal(false);
      setConfirmationState("");
    } else if (confirmationState === "submit") {
      setConfirmationModal(false);
      setConfirmationState("");
      setManuallyClaimIdPortal(true);
    }
  };

  const handleFileChange = (documentType: string, isMultiple: boolean) => (e: any) => {
    if (isMultiple) {
      const files = e?.fileList || [];
      setUploadFileList(files);
      if (files.length === 0) return;
      setSelectedFiles(
        files.map((f: any) => ({
          file: f.originFileObj,
          documentType,
        }))
      );
      setPreviewFiles(
        files.map((f: any) => ({
          url: URL.createObjectURL(f.originFileObj),
          name: f.name,
          showUpload: true,
          documentType,
        }))
      );
      setPreviewModalVisible(true);
    } else if (e?.file) {
      setSelectedFile({ file: e.file, documentType });
      setPreviewFile({
        url: URL.createObjectURL(e.file),
        name: e.file.name,
        showUpload: true,
        documentType,
      });
      setPreviewModalVisible(true);
    }
  };

  const handleAttachDocuments = async (uploadedDocs: any[]) => {
    try {
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      if (!caseId || !visitId) return;
      await updateAllDocumentsData(caseId, visitId, {
        casePreAuthAttachments: uploadedDocs,
        caseEnhancementsAttachments: [],
        caseDischargeAttachments: [],
        caseClaimAttachments: [],
      });
      await fetchDocuments();
    } catch (err: any) {
      errorMessage(err?.message || "Failed to attach documents");
      throw err;
    }
  };

  const handleDocumentUpload = async () => {
    try {
      const formData = new FormData();
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      const patientId = patients?.fetchBasicDetails?.patientResponse?.id;
      const hospitalId = patients?.fetchBasicDetails?.casesResponse?.healthCareId;
      if (hospitalId) formData.append("hospitalId", String(hospitalId));
      if (patientId) formData.append("patientId", String(patientId));
      if (caseId) formData.append("caseId", String(caseId));
      if (visitId) formData.append("visitId", String(visitId));

      if (selectedFiles.length > 0) {
        formData.append("documentType", previewFiles?.[0]?.documentType);
        selectedFiles.forEach((fileObj, index) => formData.append(`file${index}`, fileObj.file));
      } else if (selectedFile?.file) {
        formData.append("documentType", previewFile.documentType);
        formData.append("file", selectedFile.file);
      }

      const uploaded = await dispatch(
        UploadDocument({ formData, accessToken: auth?.accessToken || "" })
      ).unwrap();
      setDocumentUrls((prev) => [...prev, ...uploaded]);
      await handleAttachDocuments(uploaded);
      message.success("Document uploaded successfully");
      setPreviewModalVisible(false);
      setSelectedFile(null);
      setSelectedFiles([]);
      setUploadFileList([]);
      setPreviewFiles([]);
    } catch (err: any) {
      errorMessage(err?.message || "Failed to upload document");
    }
  };

  const handleDeleteList = async (_documentType: string, documentUrl: string) => {
    try {
      const deleteDocument = documentUrls?.find((doc: any) => doc?.documentUrl === documentUrl);
      setDocumentUrls((prev) => prev.filter((doc: any) => doc?.documentUrl !== documentUrl));
      if (deleteDocument?.documentUrl) {
        await deleteUploadedDocument({
          documentUrl: deleteDocument?.documentUrl,
          status: false,
          id: deleteDocument?.id,
        });
      }
    } catch (err: any) {
      errorMessage(err?.message || "Failed to delete document");
    }
  };

  const fetchDeficiencyData = async (_selectedDocument: any, selectedDocumentId: any) => {
    try {
      if (!selectedDocumentId) return;
      setFetchingDeficiencyData(true);
      await dispatch(getDeficiencyQuestionData(selectedDocumentId));
    } catch (err: any) {
      errorMessage(err?.message || "Failed to fetch deficiency data");
    } finally {
      setFetchingDeficiencyData(false);
    }
  };

  const handleDeficiencyModalOpen = async (_documentUrl: string, _documentId: number, document: any) => {
    try {
      await fetchDeficiencyData(document, document?.id);
      setSelectedDocument(document);
    } catch (err: any) {
      errorMessage(err?.message || "Failed to open deficiency modal");
    }
  };

  const closeDeficiencyModal = () => {
    setDeficiencyModal(false);
    setSelectedDocument(null);
  };

  const handleOpen = (docType: any, documentUrl: any) => {
    setPreviewDocument(true);
    setClickedActiveTab(docType);
    setClickedActiveDocumentUrl(documentUrl);
  };

  const isSubmitted =
    Boolean(healthIndiaPortalData?.claimId) ||
    Boolean(healthIndiaPortalData?.casePreAuthResponse?.submitted);
  const isInitiated = Boolean(healthIndiaPortalData?.casePreAuthResponse?.initiated);
  const isIntimated = Boolean(healthIndiaPortalData?.casePreAuthResponse?.intimated);

  const hasExistingRecord = Boolean(
    healthIndiaPortalData?.casePreAuthResponse?.id || healthIndiaPortalData?.id
  );
  const isApproved = Boolean(healthIndiaPortalData?.casePreAuthResponse?.approved);
  const showVerifyButton = Boolean(healthIndiaPortalData?.claimId);

  const handleClaimValidation = async () => {
    await handlePortalClaimValidationFlow({
      scheme: "HEALTH_INDIA",
      caseId: patients?.fetchBasicDetails?.casesResponse?.id,
      visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
      healthcareId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
      schemeId: patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
      claimId: healthIndiaPortalData?.claimId,
      portalData: preAuth?.portalData,
      patients,
      setIsValidationLoading,
      setValidationRes,
      setRegistrationValidationStatus,
      setIsValidationModalOpen,
    });
  };

  const handleUpdateClaimValidation = async (data: any) => {
    try {
      await updatePortalClaimValidation(
        "HEALTH_INDIA",
        patients?.fetchBasicDetails?.casesResponse?.id,
        patients?.fetchBasicDetails?.visitInfoResponse?.id,
        data,
      );
      successMessage("Details Updated Successfully");
      if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
        await dispatch(
          getBasicDetails({
            id: patients?.fetchBasicDetails?.visitInfoResponse?.id,
          }),
        );
      }
      await fetchPreAuthData();
    } catch (err) {
      errorMessage("Please, Try again later");
    }
  };

  return (
    <div>
      <PreAuthHealthIndia
        form={form}
        handleSaveConfirmationModal={handleSaveConfirmationModal}
        handleSubmit={handleSubmit}
        isSaving={isSaving}
        isIntimating={isIntimating}
        isApproving={isApproving}
        isSubmitting={isSubmitting}
        integratedWithPortal={integratedWithPortal}
        isApproved={isApproved}
        isSubmitted={isSubmitted}
        isInitiated={isInitiated}
        isIntimated={isIntimated}
        hasExistingRecord={hasExistingRecord}
        documentRequestResponseDocuments={documentRequestResponseDocuments}
        documentUrls={documentUrls}
        handleFileChange={handleFileChange}
        handleOpen={handleOpen}
        uploadFileList={uploadFileList}
        showVerifyButton={showVerifyButton}
        isValidationLoading={isValidationLoading}
        registrationValidationStatus={registrationValidationStatus}
        handleClaimValidation={handleClaimValidation}
      />

      {selectedFile?.file && !selectedFiles?.length && (
        <FilePreviewModal
          isVisible={previewModalVisible}
          onClose={() => setPreviewModalVisible(false)}
          fileUrl={previewFile.url}
          fileName={previewFile.name}
          documentType={previewFile.documentType}
          handleUpload={handleDocumentUpload}
          showUploadButton={previewFile.showUpload}
          key={selectedFile?.file?.lastModified || "no-file"}
          isLoading={enhancement?.isLoading}
        />
      )}

      {selectedFiles?.length > 0 && (
        <MultipleFilesPreviewModal
          isVisible={previewModalVisible}
          onClose={() => {
            setPreviewModalVisible(false);
            setSelectedFiles([]);
            setUploadFileList([]);
          }}
          files={previewFiles}
          handleUpload={handleDocumentUpload}
          showUploadButton={previewFiles?.some((f) => f.showUpload)}
          key={previewFiles?.map((f) => f.name).join(",") || "no-file"}
          isLoading={enhancement?.isLoading}
        />
      )}

      <PatientDetailsStageDocument
        handleCancel={() => setPreviewDocument(false)}
        handleDeleteDocument={handleDeleteList}
        open={previewDocument}
        handleDownloadDocument={async () => {}}
        claimId={healthIndiaPortalData?.claimId}
        documents={documentUrls}
        handleDeficiencyModal={handleDeficiencyModalOpen}
        closeDeficiencyModal={closeDeficiencyModal}
        deficiencyModal={deficiencyModal}
        deficiencyQuestions={patients?.deficiencyCheckQuestions}
        setDocumentName={setDocumentName}
        selectedDocument={selectedDocument}
        setUploadedDocumentUrl={setDocumentUrls}
        activeKey={activeKey}
        clickedActiveTab={clickedActiveTab}
        clickedActiveDocumentUrl={clickedActiveDocumentUrl}
        isSubmittedStatus={isSubmitted}
        isIntimated={isIntimated}
        fetchingDeficiencyData={fetchingDeficiencyData}
      />

      <DocumentDeficiencyModal
        closeDeficiencyModal={closeDeficiencyModal}
        deficiencyModal={deficiencyModal}
        documentUrl={clickedActiveDocumentUrl || ""}
        documentName={documentName}
        handleDownloadDocument={async () => {}}
        deficiencyQuestions={patients?.deficiencyCheckQuestions}
        selectedDocument={selectedDocument}
        setUploadedDocumentUrl={setDocumentUrls}
      />

      <ConfirmSubmissionModalStage
        open={confirmationModal}
        title={confirmationTitle}
        handleCancel={() => setConfirmationModal(false)}
        handleSave={handleConfirmAction}
        isLoading={isSaving || isIntimating || isApproving || isSubmitting}
      />

      <ManuallyClaimIdPortal
        open={manuallyClaimIdPortal}
        title="Enter Claim Id"
        handleCancel={() => setManuallyClaimIdPortal(false)}
        handleSave={onSubmitManually}
        footer="Save"
        isLoading={isSubmitting}
      />

      <PortalClaimValidationModal
        open={isValidationModalOpen}
        onCancel={() => setIsValidationModalOpen(false)}
        validationRes={validationRes}
        scheme="HEALTH_INDIA"
        onUpdate={handleUpdateClaimValidation}
      />
    </div>
  );
};

export default React.memo(PreAuthHealthIndiaContainer);
